'use client';

// Telegram bot connection panel for a managed agent. Three states:
//  1. Not connected → a paste-token form (validated server-side via getMe).
//  2. Connected → bot @username, enable/disable toggle, disconnect, plus the
//     chat allowlist: pending approvals (approve/reject) and approved chats
//     (revoke). Matches the agents v2 phosphor design language.
//
// The bot token is WRITE-ONLY: the server only ever tells us `hasToken`, never
// the token. There is NO platform bot token — the owner creates the bot in
// @BotFather and pastes its token here.

import { useCallback, useEffect, useState } from 'react';
import {
  TelegramLogo,
  Check,
  X,
  Trash,
  WarningCircle,
  ArrowSquareOut,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { AgentSummary, TelegramBindingSummary } from './types';

export function AgentTelegramDialog({
  open,
  onOpenChange,
  agent,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: AgentSummary | null;
  onChanged: () => void;
}) {
  const agentId = agent?.id ?? '';
  const [binding, setBinding] = useState<TelegramBindingSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/telegram`);
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Load failed (${res.status})`);
      setBinding(data.binding ?? null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    if (open) {
      setToken('');
      void load();
    }
  }, [open, load]);

  const handleConnect = async () => {
    if (!token.trim()) return;
    setConnecting(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/telegram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botToken: token.trim() }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Connect failed (${res.status})`);
      setBinding(data.binding);
      setToken('');
      toast.success(data.binding?.botUsername ? `Connected @${data.binding.botUsername}` : 'Telegram bot connected');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Connect failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleToggle = async (enabled: boolean) => {
    if (!binding) return;
    setBinding({ ...binding, enabled });
    setBusy(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/telegram`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Update failed (${res.status})`);
      setBinding(data.binding);
      onChanged();
    } catch (err) {
      setBinding((prev) => (prev ? { ...prev, enabled: !enabled } : prev));
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/telegram`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Disconnect failed (${res.status})`);
      setBinding(null);
      toast.success('Telegram bot disconnected');
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Disconnect failed');
    } finally {
      setBusy(false);
    }
  };

  const handleChat = async (chatId: string, action: 'approve' | 'reject' | 'revoke') => {
    setBusy(true);
    try {
      const res = await fetch(`/api/agents/${agentId}/telegram/chats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, action }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(typeof data?.error === 'string' ? data.error : `Update failed (${res.status})`);
      setBinding(data.binding);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TelegramLogo weight="duotone" className="size-5 text-sky-400" />
            Telegram bot
          </DialogTitle>
          <DialogDescription>
            Let people chat with {agent ? `“${agent.name}”` : 'this agent'} in Telegram. Create a bot in{' '}
            <a
              href="https://t.me/BotFather"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-sky-400 hover:underline"
            >
              @BotFather <ArrowSquareOut weight="bold" className="size-3" />
            </a>{' '}
            and paste its token. You approve who may talk to it — turns bill this agent's owner.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-8 text-center text-xs text-muted-foreground">Loading…</p>
        ) : !binding ? (
          <ConnectForm token={token} setToken={setToken} connecting={connecting} onConnect={handleConnect} />
        ) : (
          <ConnectedPanel
            binding={binding}
            busy={busy}
            onToggle={handleToggle}
            onDisconnect={handleDisconnect}
            onChat={handleChat}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ConnectForm({
  token,
  setToken,
  connecting,
  onConnect,
}: {
  token: string;
  setToken: (v: string) => void;
  connecting: boolean;
  onConnect: () => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1 text-[11px] font-medium text-muted-foreground">Bot token</p>
        <Input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="123456789:AA…"
          className="font-mono text-xs"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div className="flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-400/[0.05] px-3 py-2 text-[11px] text-muted-foreground">
        <WarningCircle weight="fill" className="mt-0.5 size-3.5 shrink-0 text-amber-400" />
        <span>
          We validate the token with Telegram, then store it encrypted. It's never shown again — rotate it in @BotFather
          if it leaks.
        </span>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={connecting || !token.trim()}
          onClick={onConnect}
          className="rounded-full bg-sky-500 px-4 font-semibold text-white hover:bg-sky-400"
        >
          {connecting ? 'Connecting…' : 'Connect bot'}
        </Button>
      </div>
    </div>
  );
}

function ConnectedPanel({
  binding,
  busy,
  onToggle,
  onDisconnect,
  onChat,
}: {
  binding: TelegramBindingSummary;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
  onDisconnect: () => void;
  onChat: (chatId: string, action: 'approve' | 'reject' | 'revoke') => void;
}) {
  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`size-1.5 rounded-full ${binding.enabled ? 'bg-emerald-400' : 'bg-muted-foreground/60'}`} />
            <p className="truncate text-sm font-semibold">
              {binding.botUsername ? `@${binding.botUsername}` : 'Connected'}
            </p>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {binding.enabled ? 'Active — responding to approved chats' : 'Disabled — not responding'}
          </p>
        </div>
        <Switch checked={binding.enabled} disabled={busy} onCheckedChange={onToggle} aria-label="Enable bot" />
      </div>

      {/* Pending approvals */}
      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Pending approvals ({binding.pendingChats.length})
        </p>
        {binding.pendingChats.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground">
            No one is waiting. When a new person messages the bot, they'll appear here for you to approve.
          </p>
        ) : (
          <div className="space-y-1.5">
            {binding.pendingChats.map((chat) => (
              <div key={chat.chatId} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-foreground/90">
                    {chat.username ? `@${chat.username}` : chat.title || `Chat ${chat.chatId}`}
                  </p>
                  <p className="truncate font-mono text-[10px] text-muted-foreground">id {chat.chatId}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onChat(chat.chatId, 'approve')}
                    className="inline-flex items-center gap-1 rounded-md border border-emerald-400/40 px-2 py-1 text-[11px] font-medium text-emerald-400 transition-colors hover:bg-emerald-400/10 disabled:opacity-50"
                  >
                    <Check weight="bold" className="size-3" /> Approve
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onChat(chat.chatId, 'reject')}
                    aria-label="Reject"
                    className="grid size-7 place-items-center rounded-md border border-border/60 text-muted-foreground transition-colors hover:border-rose-400/50 hover:text-rose-400 disabled:opacity-50"
                  >
                    <X weight="bold" className="size-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approved chats */}
      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Approved chats ({binding.approvedChatIds.length})
        </p>
        {binding.approvedChatIds.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/60 px-3 py-4 text-center text-[11px] text-muted-foreground">
            No approved chats yet.
          </p>
        ) : (
          <div className="space-y-1.5">
            {binding.approvedChatIds.map((chatId) => (
              <div key={chatId} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2">
                <p className="truncate font-mono text-[11px] text-foreground/90">{chatId}</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onChat(chatId, 'revoke')}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-rose-400/50 hover:text-rose-400 disabled:opacity-50"
                >
                  <Trash weight="bold" className="size-3" /> Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Disconnect */}
      <div className="flex justify-end border-t border-border/50 pt-3">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={onDisconnect}
          className="text-rose-400 hover:bg-rose-400/10 hover:text-rose-300"
        >
          Disconnect bot
        </Button>
      </div>
    </div>
  );
}
